import { computed, inject, signal } from '@angular/core'
import { rxResource } from '@angular/core/rxjs-interop'
import { Router } from '@angular/router'

import {
  patchState,
  signalStoreFeature,
  type,
  withComputed,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals'

import { EMPTY, Observable, lastValueFrom, of, switchMap } from 'rxjs'
import { catchError, finalize, map, tap } from 'rxjs/operators'

import { UserFormValue } from '@flowx/designer/modules/access-management/access-management.utils'
import { flattenUserAssets } from '@flowx/designer/modules/workspaces/shared/transform-utils'
import {
  AccessManagementUser,
  AccessManagementUserSummary,
} from '@flowx/designer/modules/workspaces/shared/workspace.utils'
import { selectedUserId } from '@flowx/designer/shared/store/selectors/router.selectors'

import { UsersGridSearch } from './users-grid-search'

import { WorkspaceUsersDeps } from '../access-management-store.types'

const errorTitle = (error): string => (error.error?.title ? error.error.title : 'Error message')

/**
 * Users feature for the workspace scope. The workspace exposes a single concrete users API, so
 * there is no user type to track, no grid re-creation on type change, and no `as` casts.
 *
 * The `designerUser`/`userType` parameters on some methods are kept for signature parity with the
 * org-admin feature (so shared components can call either through a common contract); the
 * workspace scope simply ignores them — one API serves every user.
 */
// The return type of signalStoreFeature is an inferred generic that must not be widened.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
export function withWorkspaceUsersFeature() {
  return signalStoreFeature(
    { props: type<WorkspaceUsersDeps>() },
    withState({ usersLoading: false }),
    withProps((store) => ({
      selectedUserId: store._ngrxStore.selectSignal(selectedUserId),
    })),
    withProps((store) => {
      const router = inject(Router)

      return {
        // The workspace scope never switches user type, so the grid search is created once.
        // Exposed as a signal so callers read it as `usersGridSearch()`, matching the org-admin
        // feature's `linkedSignal`-backed surface.
        usersGridSearch: signal(
          new UsersGridSearch(
            store._usersApi,
            router.routerState.snapshot.root.queryParams?.searchQuery
          )
        ),
      }
    }),
    withProps((store) => ({
      selectedUserResource: rxResource({
        params: () => ({ selectedUserId: store.selectedUserId() }),
        stream: ({ params: { selectedUserId } }) => {
          if (!selectedUserId) {
            return of(null)
          }

          patchState(store, { usersLoading: true })

          return store._usersApi.getUser(selectedUserId).pipe(
            finalize(() => patchState(store, { usersLoading: false })),
            catchError((error) => {
              store._toastMessageService.showErrorMessage(
                `Failed to get user: ${errorTitle(error)}`
              )
              return EMPTY
            })
          )
        },
      }),
      userGroupsResource: rxResource({
        params: () => ({ selectedUserId: store.selectedUserId() }),
        stream: ({ params: { selectedUserId } }) => {
          if (!selectedUserId) {
            return of([])
          }

          patchState(store, { usersLoading: true })

          return store._usersApi.getUserGroups(selectedUserId).pipe(
            finalize(() => patchState(store, { usersLoading: false })),
            catchError((error) => {
              store._toastMessageService.showErrorMessage(
                `Failed to get user groups: ${errorTitle(error)}`
              )
              return EMPTY
            })
          )
        },
      }),
      userAssetsResource: rxResource({
        params: () => ({ selectedUserId: store.selectedUserId() }),
        stream: ({ params: { selectedUserId } }) => {
          if (!selectedUserId) {
            return of([])
          }

          patchState(store, { usersLoading: true })

          return store._usersApi.getUserAssets(selectedUserId).pipe(
            switchMap((userAssets) =>
              store._applicationsService
                .getApplicationSummaryByIds(userAssets.map((access) => access.id))
                .pipe(map((applicationSummaries) => ({ userAssets, applicationSummaries })))
            ),
            finalize(() => patchState(store, { usersLoading: false })),
            map(({ userAssets, applicationSummaries }) =>
              flattenUserAssets(userAssets, applicationSummaries)
            ),
            catchError((error) => {
              store._toastMessageService.showErrorMessage(
                `Failed to get workspace user assets: ${errorTitle(error)}`
              )
              return EMPTY
            })
          )
        },
      }),
    })),
    withComputed((store) => ({
      selectedUser: computed(() =>
        store.selectedUserResource.hasValue() ? store.selectedUserResource.value() : null
      ),
      userGroups: computed(() => store.userGroupsResource.value()),
      userAssets: computed(() => store.userAssetsResource.value()),
    })),
    withMethods((store) => ({
      searchUsers(filterKey: string): void {
        store.usersGridSearch().search({ filterKey })
      },
      refreshUsersGrid(): void {
        store.usersGridSearch().refresh()
      },
      getUserForEditing(userId: string): Observable<AccessManagementUser> {
        return store._usersApi.getUserForEditing(userId)
      },
      createUser(payload: UserFormValue): Observable<AccessManagementUser> {
        return store._usersApi.createUser(payload)
      },
      updatePassword(userId: string, password: string): Observable<void> {
        return store._usersApi.updatePassword(userId, password)
      },
      updateUserState(userId: string, enabled: boolean): Observable<void> {
        return store._usersApi
          .updateUserState(userId, { enabled })
          .pipe(tap(() => store.usersGridSearch().refresh()))
      },
      // The shared contract types `resendInvite` with a `userType` arg (the org-admin signature);
      // this single-param version stays assignable to it, and the workspace API ignores the arg
      // callers pass through the token.
      resendInvite(email: string): Observable<void> {
        return store._usersApi.resendInvite(email).pipe(
          tap(() => store.usersGridSearch().refresh()),
          catchError(() => {
            store._toastMessageService.showErrorMessage(
              `Failed to resend invite for user: ${email}`
            )
            return EMPTY
          })
        )
      },
      deleteUser(userId: string, userName: string): Observable<void> {
        return store._usersApi.deleteUser(userId).pipe(
          tap(() => {
            store.usersGridSearch().refresh()
            store._toastMessageService.showSuccessMessage(
              `User ${userName} was deleted. Access to all workspaces, projects and libraries was revoked.`
            )
          }),
          catchError((error) => {
            store._toastMessageService.showErrorMessage(
              `Failed to delete user ${userName}: ${errorTitle(error)}`
            )
            return EMPTY
          })
        )
      },
      getUserSummaries(): Observable<AccessManagementUserSummary[]> {
        patchState(store, { usersLoading: true })

        return store._usersApi.getUserSummaries().pipe(
          finalize(() => patchState(store, { usersLoading: false })),
          catchError((error) => {
            store._toastMessageService.showErrorMessage(`Failed to get users: ${errorTitle(error)}`)
            return EMPTY
          })
        )
      },
      removeUserAsset: async (assetId: string, roleId: string): Promise<void> => {
        try {
          const selectedUserId = store.selectedUserId()

          await lastValueFrom(
            store._usersApi.removeWorkspaceUserAsset(assetId, selectedUserId, roleId)
          )

          store.userAssetsResource.update((userAssets) =>
            userAssets.filter((asset) => asset.id !== `${assetId}-${roleId}`)
          )

          store._toastMessageService.showSuccessMessage('Access removed successfully')
        } catch (error) {
          store._toastMessageService.showErrorMessage(
            `An error occurred while removing access: ${errorTitle(error)}`
          )
        }
      },
    })),
    withHooks({
      onDestroy(store) {
        store.usersGridSearch()?.manualDestroy()
      },
    })
  )
}
